import { useState, useEffect, useRef } from 'react';
import { CheckCircle, X, CreditCard, BookOpen } from 'lucide-react';
import { payCreateQR, payStatus } from '../api';
import { useAuth } from '../context/AuthContext';

export default function Pricing() {
  const { isLoggedIn, isPaid } = useAuth();
  const [showQR,setShowQR] = useState(false);
  const [txnRef,setTxnRef] = useState('');
  const [status,setStatus] = useState('');
  const [loading,setLoad]  = useState(false);
  const pollRef            = useRef(null);

  const startPayment = async()=>{
    if(!isLoggedIn()){ alert('Please login first to subscribe.'); return; }
    setLoad(true);
    try{
      const r=await payCreateQR();
      setTxnRef(r.data.txnRef); setShowQR(true); setStatus('pending');
    }catch{ alert('Failed to generate payment QR. Please try again.'); }
    finally{ setLoad(false); }
  };

  useEffect(()=>{
    if(!txnRef||!showQR) return;
    pollRef.current=setInterval(async()=>{
      try{
        const r=await payStatus(txnRef);
        if(r.data.status==='success'){ setStatus('success'); clearInterval(pollRef.current); }
        if(r.data.status==='failed'){  setStatus('failed');  clearInterval(pollRef.current); }
      }catch{}
    },3000);
    return()=>clearInterval(pollRef.current);
  },[txnRef,showQR]);

  return(
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-blue-950 mb-2">Simple, Transparent Pricing</h1>
        <p className="text-gray-400">One annual subscription. Full access to everything.</p>
      </div>
      {isPaid()&&(
        <div className="mb-8 bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
          <CheckCircle size={32} className="mx-auto text-green-500 mb-2"/>
          <p className="font-bold text-green-800 text-lg">You have an active subscription!</p>
          <p className="text-sm text-green-600 mt-1">Enjoy full access to all documents and features.</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="text-xs font-bold text-gray-400 mb-1">FREE</div>
          <div className="text-4xl font-bold text-gray-800 mb-4">$0</div>
          {[[true,'Search all documents'],[true,'AI chat with document references'],[true,'Save to collection & tags'],[false,'Read full documents'],[false,'Download PDF files']].map(([ok,f],i)=>(
            <div key={i} className={"flex items-center gap-2 text-sm mb-2 "+(ok?'text-gray-700':'text-gray-300')}>{ok?<CheckCircle size={15} className="text-green-500"/>:<X size={15}/>}{f}</div>
          ))}
          <div className="mt-5 w-full border border-gray-200 text-gray-400 py-2.5 rounded-xl text-sm text-center">Free Plan</div>
        </div>
        <div className="bg-blue-950 text-white rounded-2xl p-6 relative shadow-xl">
          <div className="absolute top-4 right-4 bg-yellow-500 text-blue-950 text-xs font-bold px-2 py-0.5 rounded-full">POPULAR</div>
          <div className="text-xs font-bold text-yellow-400 mb-1">ANNUAL SUBSCRIPTION</div>
          <div className="text-4xl font-bold mb-1">$49<span className="text-base font-normal text-blue-300">/year</span></div>
          <div className="text-xs text-blue-400 mb-4">Pay once · Access all year</div>
          {['Search all documents','AI chat with doc references','Save to collection & tags','Read full documents','Download PDF files'].map((f,i)=>(
            <div key={i} className="flex items-center gap-2 text-sm mb-2 text-blue-100"><CheckCircle size={15} className="text-yellow-400"/>{f}</div>
          ))}
          <button onClick={startPayment} disabled={loading||isPaid()} className="mt-5 w-full bg-yellow-500 text-blue-950 py-3 rounded-xl font-bold hover:bg-yellow-400 disabled:opacity-60 transition flex items-center justify-center gap-2">
            <CreditCard size={16}/>{loading?'Generating QR...':isPaid()?'Already Subscribed':'Pay with ABA Bank'}
          </button>
        </div>
      </div>
      <p className="text-center text-xs text-gray-400 mt-6">🔒 Secure payment via ABA PayWay · Contact us for questions</p>

      {showQR&&(
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-xs w-full shadow-2xl text-center">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><div className="w-7 h-7 bg-blue-900 rounded-lg flex items-center justify-center"><BookOpen size={13} className="text-white"/></div><span className="font-bold text-gray-800 text-sm">Pay with ABA</span></div>
              <button onClick={()=>{setShowQR(false);clearInterval(pollRef.current);}}><X size={18} className="text-gray-400"/></button>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 mb-4"><div className="text-xs text-gray-400">Annual Subscription</div><div className="text-3xl font-bold text-blue-900">$49.00</div><div className="text-xs text-gray-400">Law of Cambodia (LawOC)</div></div>
            {status==='success'?(
              <div className="py-6"><CheckCircle size={48} className="mx-auto text-green-500 mb-3"/><p className="font-bold text-green-700 text-lg">Payment Successful!</p><p className="text-sm text-gray-500 mb-4">Your account has been upgraded to paid.</p><button onClick={()=>{setShowQR(false);window.location.reload();}} className="bg-green-500 text-white px-6 py-2 rounded-xl font-semibold">Continue</button></div>
            ):status==='failed'?(
              <div className="py-6"><X size={48} className="mx-auto text-red-500 mb-3"/><p className="font-bold text-red-700">Payment Failed</p><button onClick={()=>{setShowQR(false);setStatus('');}} className="mt-3 bg-gray-100 text-gray-600 px-6 py-2 rounded-xl text-sm">Try Again</button></div>
            ):(
              <>
                <div className="w-44 h-44 mx-auto border-2 border-gray-200 rounded-xl mb-4 flex items-center justify-center bg-white relative">
                  <svg width="160" height="160" viewBox="0 0 160 160" className="absolute inset-0">{Array.from({length:10},(_,i)=>Array.from({length:10},(_,j)=>((i+j*3)%2===0)?<rect key={`${i}-${j}`} x={8+i*14} y={8+j*14} width="12" height="12" fill="#172554" rx="2"/>:null)).flat()}</svg>
                  <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center z-10 shadow"><BookOpen size={18} className="text-blue-950"/></div>
                </div>
                <p className="text-sm text-gray-600 mb-1">Open <strong>ABA Mobile</strong> and scan QR</p>
                <p className="text-xs text-gray-400 mb-3 animate-pulse">Waiting for payment confirmation...</p>
                <div className="flex justify-center gap-4 text-xs text-gray-400"><span className="flex items-center gap-1"><CheckCircle size={11} className="text-green-500"/>Secure</span><span className="flex items-center gap-1"><CheckCircle size={11} className="text-green-500"/>Instant</span></div>
              </>
            )}
            <div className="border-t pt-3 mt-3"><p className="text-xs text-gray-400">Issues? <span className="text-blue-600 cursor-pointer underline">Request manual approval</span></p></div>
          </div>
        </div>
      )}
    </div>
  );
}
